'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/contexts/LanguageContext';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

export default function Navigation() {
	const { t, language, setLanguage } = useLanguage();
	const pathname = usePathname();
	const navRef = useRef<HTMLElement>(null);
	const barRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const openRef = useRef(false);
	openRef.current = open;

	// Sur la page d'accueil, ancres locales ; ailleurs, retour à l'accueil + ancre.
	const base = pathname === '/' ? '' : '/';
	const items = [
		{ label: t.nav.services, href: `${base}#services` },
		{ label: t.nav.realizations, href: `${base}#realizations` },
		{ label: t.nav.about, href: `${base}#about` },
		{ label: t.nav.contact, href: `${base}#contact` },
	];
	const cta = `${base}#contact`;
	const home = pathname === '/' ? '#home' : '/';
	const toggleLang = () => setLanguage(language === 'fr' ? 'en' : 'fr');

	useEffect(() => {
		const nav = navRef.current;
		const bar = barRef.current;
		if (!nav || !bar) return;
		const motion = document.documentElement.classList.contains('motion');
		let lastY = window.scrollY;
		let ticking = false;
		const onScroll = () => {
			const y = window.scrollY;
			nav.classList.toggle('scrolled', y > 8);
			if (motion) {
				// transform uniquement : la barre se masque en descendant, revient en remontant ou au focus
				nav.classList.toggle('hide', y > lastY && y > 420 && !openRef.current);
				const h = document.documentElement.scrollHeight - window.innerHeight;
				bar.style.transform = `scaleX(${h > 0 ? y / h : 0})`;
			}
			lastY = y;
			ticking = false;
		};
		const onScrollRaf = () => {
			if (!ticking) {
				ticking = true;
				requestAnimationFrame(onScroll);
			}
		};
		const show = () => nav.classList.remove('hide');
		onScroll();
		window.addEventListener('scroll', onScrollRaf, { passive: true });
		nav.addEventListener('focusin', show);
		return () => {
			window.removeEventListener('scroll', onScrollRaf);
			nav.removeEventListener('focusin', show);
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);

	return (
		<>
			<a className="skip" href="#main-content">{t.nav.skip}</a>
			<div className="progress" ref={barRef} aria-hidden="true" />

			<header className="nav" ref={navRef}>
				<div className="wrap nav-in">
					<a className="brand" href={home} aria-label={t.nav.homeLabel}>
						<Logo />
						<span>Kuba <span className="gt">Studio</span></span>
					</a>
					<nav className="links" aria-label={t.nav.mainLabel}>
						{items.map((item) => (
							<a key={item.href} href={item.href}>{item.label}</a>
						))}
					</nav>
					<div className="nav-r">
						<button className="lang" type="button" aria-label={t.nav.langSwitchLabel} onClick={toggleLang}>{t.nav.langShort}</button>
						<ThemeToggle />
						<a className="btn btn-p btn-sm nav-cta" href={cta}>{t.nav.cta}</a>
						<button className="icon-btn burger" type="button" aria-expanded={open} aria-controls="mobile-menu"
							aria-label={open ? t.nav.closeMenu : t.nav.openMenu} onClick={() => setOpen(!open)}>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
								<path d={open ? 'M6 6l12 12M18 6 6 18' : 'M4 8h16M4 16h16'} />
							</svg>
						</button>
					</div>
				</div>
			</header>

			<div className={`mobile${open ? ' open' : ''}`} id="mobile-menu" onClick={(e) => { if ((e.target as Element).closest('a')) setOpen(false); }}>
				{items.map((item) => (
					<a key={item.href} href={item.href} tabIndex={open ? 0 : -1}>{item.label}</a>
				))}
				<button type="button" className="lang-m" tabIndex={open ? 0 : -1} aria-label={t.nav.langSwitchLabel} onClick={toggleLang}>
					{language === 'fr' ? 'English' : 'Français'}
				</button>
				<a className="btn btn-p" href={cta} tabIndex={open ? 0 : -1}>{t.nav.cta}</a>
			</div>
		</>
	);
}
